import type { FC } from 'react'

import { Input, Select, Checkbox, Textarea, StyledText } from '@skytech/ui'
import { Button } from '@skytech/ui/Button'
import { Disclosure } from '@skytech/ui/Disclosure'
import { getEntries, LOCALE_WITH_NAMES } from '@skytech/utils'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { FieldProps } from 'formik'
import { Form, Field, Formik } from 'formik'
import { useParams } from 'react-router-dom'

import { Translation } from '@/app/translations/Translation'
import { useTranslation } from '@/app/translations/translations-hooks'
import { USER_TYPE } from '@/app-constants'
import { LocalDateTime } from '@/components/LocalDateTime'
import { useProfile } from '@/hooks/user'
import queries from '@/modules/admin/queries'
import adminResources from '@/modules/admin/resources'
import type { CostObjectDetails } from '@/modules/admin/resources'
import { commonEcomPolicyResources } from '@/resources/ecom-policy'
import { commonQueries } from '@/resources/queries'
import { commonSubscriptionResources } from '@/resources/subscriptions'

import Actions from './Actions'
import AssetsSneakPeek from './AssetsSneakPeek'

import './index.styles.scss'

const Profile: FC<{ costObjectDetails: CostObjectDetails; onGoToAssetTab: () => void }> = ({
  costObjectDetails,
  onGoToAssetTab,
}) => {
  const queryClient = useQueryClient()
  const translate = useTranslation()
  const params = useParams()
  const profile = useProfile()
  const hasDevicePolicy = Boolean(profile?.hasDevicePolicy)
  const costObjectId = Number(params.costObjectId)

  if (isNaN(costObjectId)) throw Error(translate('Employee is not a number!'))

  const costObjectMutation = useMutation(adminResources.postCostObject, {
    onSuccess() {
      queryClient.invalidateQueries(queries.adminCostObjectDetails(costObjectId))
      queryClient.invalidateQueries(queries.adminCostObjectList(), { refetchType: 'all' })
    },
  })

  const { data: customerDepartmentList, isLoading: isCustomerDepartmentListLoading } = useQuery(
    commonQueries.customerDepartmentList(costObjectDetails.customerId),
    () => commonEcomPolicyResources.getCustomerDepartmentList(costObjectDetails.customerId)
  )

  const { data: setup, isLoading: isSetupLoading } = useQuery(
    commonQueries.subscriptionSetup(),
    commonSubscriptionResources.getSetup
  )
  const benefitOptions = USER_TYPE.map(type => ({
    value: type.symbol,
    label: translate(`cost_object_benefit_type/${type.symbol}`),
  }))
  const devicePolicyOptions = setup?.devicePolicies
    ?.filter(
      policy =>
        (policy.customerHeadId || policy.customerId === costObjectDetails?.customerId) &&
        policy.departmentId === costObjectDetails.departmentId
    )
    .map(policy => ({
      value: policy.id,
      label: policy.name,
    }))

  const departmentOptions = customerDepartmentList?.map(department => ({
    value: department.id,
    label: [department.code, department.name].join(' - '),
  }))
  const salaryDeductionProfileOptions =
    costObjectDetails?.salaryDeductionProfiles.map(profile => ({
      value: profile.id,
      label: profile.name,
    })) || []
  const telePolicyOptions = [
    ...salaryDeductionProfileOptions,
    {
      value: null,
      label: translate('No policy'),
    },
  ]
  const localeOptions = getEntries(LOCALE_WITH_NAMES).map(([locale, label]) => ({
    value: locale,
    label: translate(label),
  }))

  return (
    <div className="profile">
      <div className="static-info">
        <div className="fixed-data">
          <h3>
            <Translation id="User information" />
          </h3>

          <StyledText colorMode="gray" size="small" capitalize block marginBottom={2}>
            <Translation id="Company" />
          </StyledText>
          <StyledText block colorMode="black" marginBottom={8}>
            {costObjectDetails.customerName}
          </StyledText>

          <StyledText colorMode="gray" size="small" capitalize block marginBottom={2}>
            <Translation id="Phone number" />
          </StyledText>
          <StyledText
            block
            colorMode="black"
            marginBottom={8}
          >{`+${costObjectDetails.countryCode} ${costObjectDetails.code}`}</StyledText>

          <StyledText colorMode="gray" size="small" capitalize block marginBottom={2}>
            <Translation id="Created" />
          </StyledText>
          <div className="mb-2 flex items-center gap-1">
            <StyledText block colorMode="black">
              {costObjectDetails.creatorName}
            </StyledText>
            <StyledText colorMode="gray" size="small" capitalize block>
              <LocalDateTime date={costObjectDetails.createdDate} />
            </StyledText>
          </div>

          <StyledText colorMode="gray" size="small" capitalize block marginBottom={2}>
            <Translation id="Last edited" />
          </StyledText>
          <div className="mb-2 flex items-center gap-1">
            <StyledText block colorMode="black">
              {costObjectDetails.editorName}
            </StyledText>
            <StyledText colorMode="gray" size="small" capitalize block>
              <LocalDateTime date={costObjectDetails.editedDate} />
            </StyledText>
          </div>

          {costObjectDetails.workLocation && (
            <>
              <StyledText colorMode="gray" size="small" capitalize block marginBottom={2}>
                <Translation id="Work location" />
              </StyledText>
              <StyledText block colorMode="black">
                {costObjectDetails.workLocation}
              </StyledText>
            </>
          )}
        </div>

        <Actions costObjectId={Number(costObjectId)} type={costObjectDetails.type} />

        <AssetsSneakPeek costObjectId={Number(costObjectId)} onGoToAssetTab={onGoToAssetTab} />
      </div>

      <div className="divider" />

      <Formik
        initialValues={{
          name: costObjectDetails.name || '',
          locale: costObjectDetails.locale,
          departmentId: costObjectDetails.departmentId,
          employeeNo: costObjectDetails.employeeNo || '',
          email: costObjectDetails.email,
          benefitType: costObjectDetails.benefitType,
          isHidden: Boolean(costObjectDetails.isHidden),
          salaryDeductionProfileId: costObjectDetails.salaryDeductionProfileId,
          devicePolicyId: costObjectDetails.devicePolicyId,
          fixedSalaryDeductionComment: costObjectDetails.fixedSalaryDeductionComment || '',
          appMessage: costObjectDetails.appMessage || '',
          accountingCode: costObjectDetails.accountingCode || '',
          dim1: costObjectDetails.dim1 || '',
          dim2: costObjectDetails.dim2 || '',
          dim3: costObjectDetails.dim3 || '',
          dim4: costObjectDetails.dim4 || '',
        }}
        validate={formValues => {
          const errors: Partial<Record<keyof CostObjectDetails, string>> = {}

          if (!formValues.name) errors.name = translate('This field is mandatory')
          // Email should only be mandatory when benefit type is "free". Optional otherwise.
          if (formValues.benefitType === 'F' && !formValues.email)
            errors.email = translate('This field is mandatory')
          return errors
        }}
        onSubmit={formValues => {
          if (!costObjectId) return

          costObjectMutation.mutate({
            id: Number(costObjectId),
            payload: formValues,
          })
        }}
      >
        <Form>
          <div className="profile-sections-grid">
            <div>
              <Field name="name">
                {({ field, meta }: FieldProps) => (
                  <Input
                    isFullWidth
                    capitalizeLabel
                    id="name"
                    label={
                      <>
                        <Translation id="Name" />*
                      </>
                    }
                    marginBottom={16}
                    error={meta.error}
                    touched={meta.touched}
                    {...field}
                  />
                )}
              </Field>

              <Field name="email">
                {({ field, meta }: FieldProps) => (
                  <Input
                    isFullWidth
                    id="email"
                    label={
                      <>
                        <Translation id="Email" />*
                      </>
                    }
                    marginBottom={16}
                    error={meta.error}
                    touched={meta.touched}
                    {...field}
                  />
                )}
              </Field>

              <Field name="employeeNo">
                {({ field, meta }: FieldProps) => (
                  <Input
                    isFullWidth
                    id="employeeNo"
                    label={<Translation id="Employee number" />}
                    marginBottom={16}
                    error={meta.error}
                    touched={meta.touched}
                    {...field}
                  />
                )}
              </Field>

              <Field name="accountingCode">
                {({ field, meta }: FieldProps) => (
                  <Input
                    isFullWidth
                    id="accountingCode"
                    label={<Translation id="Accounting code" />}
                    marginBottom={16}
                    error={meta.error}
                    touched={meta.touched}
                    {...field}
                  />
                )}
              </Field>

              <Disclosure>
                {({ open }) => (
                  <>
                    <Disclosure.Button isOpen={open} className="justify-center px-0">
                      <Translation id="Show Dim-values" />
                    </Disclosure.Button>

                    <Disclosure.Panel className="p-0">
                      <Field name="dim1">
                        {({ field, meta }: FieldProps) => (
                          <Input
                            isFullWidth
                            id="dim1"
                            label={<Translation id="Dim 1" />}
                            marginBottom={16}
                            error={meta.error}
                            touched={meta.touched}
                            {...field}
                          />
                        )}
                      </Field>
                      <Field name="dim2">
                        {({ field, meta }: FieldProps) => (
                          <Input
                            isFullWidth
                            id="dim2"
                            label={<Translation id="Dim 2" />}
                            marginBottom={16}
                            error={meta.error}
                            touched={meta.touched}
                            {...field}
                          />
                        )}
                      </Field>

                      <Field name="dim3">
                        {({ field, meta }: FieldProps) => (
                          <Input
                            isFullWidth
                            id="dim3"
                            label={<Translation id="Dim 3" />}
                            marginBottom={16}
                            error={meta.error}
                            touched={meta.touched}
                            {...field}
                          />
                        )}
                      </Field>
                      <Field name="dim4">
                        {({ field, meta }: FieldProps) => (
                          <Input
                            isFullWidth
                            id="dim4"
                            label={<Translation id="Dim 4" />}
                            marginBottom={16}
                            error={meta.error}
                            touched={meta.touched}
                            {...field}
                          />
                        )}
                      </Field>
                    </Disclosure.Panel>
                  </>
                )}
              </Disclosure>
            </div>

            <div>
              <Field name="departmentId">
                {({ field, form }: FieldProps) => (
                  <Select
                    id="departmentId"
                    label={<Translation id="Department" />}
                    options={departmentOptions || []}
                    marginBottom={16}
                    {...field}
                    isLoading={isCustomerDepartmentListLoading}
                    onChange={option => form.setFieldValue(field.name, option?.value)}
                  />
                )}
              </Field>

              <Field name="locale">
                {({ field, form }: FieldProps) => (
                  <Select
                    id="locale"
                    label={<Translation id="Language" />}
                    options={localeOptions || []}
                    marginBottom={16}
                    {...field}
                    isLoading={isCustomerDepartmentListLoading}
                    onChange={option => form.setFieldValue(field.name, option?.value)}
                  />
                )}
              </Field>

              <Field name="benefitType">
                {({ field, form }: FieldProps) => (
                  <Select
                    id="benefitType"
                    label={<Translation id="Benefit type" />}
                    options={benefitOptions || []}
                    marginBottom={16}
                    {...field}
                    isLoading={isCustomerDepartmentListLoading}
                    onChange={option => form.setFieldValue(field.name, option?.value)}
                  />
                )}
              </Field>

              <Field name="salaryDeductionProfileId">
                {({ field, form }: FieldProps) => (
                  <Select
                    id="salaryDeductionProfileId"
                    label={<Translation id="Tele policy" />}
                    options={telePolicyOptions}
                    marginBottom={16}
                    {...field}
                    onChange={option => form.setFieldValue(field.name, option?.value)}
                  />
                )}
              </Field>

              {hasDevicePolicy && (
                <Field name="devicePolicyId">
                  {({ field, form }: FieldProps) => (
                    <Select
                      id="devicePolicyId"
                      label={<Translation id="Device policy" />}
                      options={devicePolicyOptions || []}
                      marginBottom={16}
                      isLoading={isSetupLoading}
                      {...field}
                      onChange={option => form.setFieldValue(field.name, option?.value)}
                    />
                  )}
                </Field>
              )}
            </div>

            <div>
              <Field name="fixedSalaryDeductionComment">
                {({ field, meta }: FieldProps) => (
                  <Textarea
                    id="fixedSalaryDeductionComment"
                    marginBottom={16}
                    label={<Translation id="Comment" />}
                    error={meta.error}
                    touched={meta.touched}
                    {...field}
                  />
                )}
              </Field>
              <Field name="appMessage">
                {({ field, meta }: FieldProps) => (
                  <Textarea
                    id="appMessage"
                    marginBottom={16}
                    label={<Translation id="Message to employee" />}
                    error={meta.error}
                    touched={meta.touched}
                    placeholder={translate(
                      'Here you can insert a message to the employee. It will appear on the next awareness email and in the control app.'
                    )}
                    {...field}
                  />
                )}
              </Field>
            </div>
          </div>

          <footer>
            <div className={`is-user-hidden ${costObjectDetails.isHidden ? 'is-hidden' : ''}`}>
              <Field name="isHidden">
                {({ field }: FieldProps) => (
                  <Checkbox
                    id="isHidden"
                    marginTop={0}
                    label={<Translation id="Is user hidden" />}
                    checked={field.value}
                    {...field}
                  />
                )}
              </Field>
            </div>

            <Button
              type="submit"
              variant="primary"
              disabled={costObjectMutation.isLoading}
              isLoading={costObjectMutation.isLoading}
            >
              <Translation id="save" />
            </Button>
          </footer>
        </Form>
      </Formik>
    </div>
  )
}

export default Profile
